import {AppBar, Box, ButtonBase, Toolbar, Typography} from '@mui/material';
import {MagnifyingGlass} from '@phosphor-icons/react';
import {messages} from '@smartmixin/i18n';
import {useIntl} from 'react-intl';
import {useLocation} from 'react-router';
import Logo from '../../components/Logo';
import {IconButton} from '../../components/ui';
import {ThemeToggleButton} from '../../navigation/layout/ThemeToggleButton';
import {useNavItems} from '../../navigation/layout/useNavItems';
import {shell} from '../../theme/tokens';
import UserMenu from './UserMenu';

type Props = {
  /** Signed in: phone only (<900); the sidebar / rail take over above. */
  signedIn: boolean;
};

const BRAND = 'SmartMixin';

/**
 * The top bar (56px). Signed out it is the whole navigation at every width
 * (destination links from 900px; below that the bottom bar carries them on
 * the app routes). Signed in it is the phone header only: logo, search,
 * theme, the account menu.
 *
 * The signed-out links follow target 5d's public bar: text.secondary at
 * medium weight, the current one in text.primary with a 2px ink underline
 * at the bar's foot and aria-current="page".
 */
const AppNavBar = ({signedIn}: Props) => {
  const {formatMessage} = useIntl();
  const {pathname} = useLocation();
  const items = useNavItems();
  const onSearch = pathname.startsWith('/app/stations/search');
  // As the sidebar's: short, the search page names what it searches.
  const searchLabel = formatMessage(messages.common.search);
  const links = [items.stations, items.map, items.help];
  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        display: signedIn ? {xs: 'flex', md: 'none'} : 'flex',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}>
      {/* `&&` doubles the class: MUI's Toolbar sets 64px in a
          min-width:600px media rule that a plain sx minHeight loses to, and
          the content offset (--shell-top) is 56. 55 + the 1px hairline. */}
      <Toolbar sx={{'&&': {minHeight: shell.topBarHeight - 1}}}>
        {/* One link home: the logo, and the wordmark from 600px. Named by
            the brand, which phones do not show. */}
        <ButtonBase
          href="/"
          aria-label={BRAND}
          disableRipple
          sx={{
            gap: 1.25,
            px: 0.5,
            py: 0.5,
            ml: -0.5,
            borderRadius: 1,
            color: 'text.primary',
          }}>
          <Logo size={26} alt="" />
          <Typography
            component="span"
            variant="title"
            sx={{display: {xs: 'none', sm: 'block'}}}>
            {BRAND}
          </Typography>
        </ButtonBase>

        {!signedIn && (
          <Box
            component="nav"
            aria-label={formatMessage(messages.navigation.main)}
            sx={{
              ml: 3,
              alignSelf: 'stretch',
              gap: 0.5,
              display: {xs: 'none', md: 'flex'},
            }}>
            {links.map((item) => {
              const current = item.isActive(pathname);
              return (
                <ButtonBase
                  key={item.key}
                  href={item.to}
                  aria-current={current ? 'page' : undefined}
                  disableRipple
                  sx={{
                    px: 1,
                    typography: 'body2',
                    fontWeight: 'fontWeightMedium',
                    color: 'text.secondary',
                    '&:hover': {color: 'text.primary'},
                    '&[aria-current="page"]': {
                      color: 'text.primary',
                      boxShadow: (theme) =>
                        `inset 0 -2px 0 ${theme.palette.ink.main}`,
                    },
                    // Inset: the link is exactly the bar's height.
                    '&&:focus-visible': {outlineOffset: -2},
                  }}>
                  {item.label}
                </ButtonBase>
              );
            })}
          </Box>
        )}

        <Box sx={{flexGrow: 1}} />

        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          {/* Phones only. Signed in, the sidebar and rail carry search from
              900px; signed out, the top bar's links do not include it. */}
          <IconButton
            href="/app/stations/search"
            aria-label={searchLabel}
            aria-current={onSearch ? 'page' : undefined}
            sx={{display: {xs: 'inline-flex', md: 'none'}}}>
            <MagnifyingGlass />
          </IconButton>
          <ThemeToggleButton />
          <UserMenu />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AppNavBar;
